// Initialises product analytics (PostHog) on the frontend. Called once
// from main.tsx alongside Sentry. The functions exported below
// (`identify`, `reset`, `track`) are safe to call from anywhere in the
// app at any time — they no-op when VITE_POSTHOG_KEY is unset (dev /
// test / CI / preview builds without analytics configured), and queue
// briefly while the lazy chunk loads when it *is* configured.
//
// Why PostHog and not Plausible: see DECISIONS.md (issue #67 thread).
// Short version: Luno Learning's pre-launch questions are funnel-shaped
// ("what % of signups finish onboarding / generate a first course /
// come back?") and PostHog answers those natively via per-user
// identity. Session replay is deliberately **off** — re-enable per
// investigation when a specific question needs it.
//
// Why dynamic import: posthog-js ships ~63 KB gzipped, large enough
// that loading it eagerly in the main bundle hits course-player's
// already-constrained LCP. Splitting it into its own chunk via
// `import('posthog-js')` keeps the LCP-blocking path lean — events
// fired before the chunk arrives are queued and replayed on init.
//
// Consent: PECR / ICO 2023 guidance requires prior consent for first-
// party analytics. PostHog is initialised in opt-out mode by default,
// so even though the SDK loads (we need it ready for instant opt-in
// once the user clicks "Accept"), no events are sent until
// `optInAnalytics()` runs. CookieBanner.tsx drives that flow on first
// visit and persists the choice in localStorage.
import type { PostHog } from 'posthog-js'

let client: PostHog | null = null
// True once initAnalytics has decided whether analytics is configured.
// False before that (helpers no-op) and false forever in builds where
// VITE_POSTHOG_KEY is unset.
let analyticsConfigured = false
// Pending calls that fired between initAnalytics() returning and the
// posthog-js chunk finishing its dynamic import. Drained on load.
// Bounded so a misconfigured failed-load case can't grow unboundedly.
const QUEUE_CAP = 50
const queue: Array<(c: PostHog) => void> = []

export const CONSENT_STORAGE_KEY = 'cookie_consent'
export type ConsentValue = 'accepted' | 'rejected'

// Read the persisted consent state. Returns null when the user hasn't made
// a choice yet (banner should show) or when localStorage is unavailable
// (private-browsing edge case — treat as no consent rather than throwing).
export function getConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (v === 'accepted' || v === 'rejected') return v
    return null
  } catch {
    return null
  }
}

// Persist a consent choice. The banner calls this on Accept / Reject; the
// caller is also expected to call `optInAnalytics` / `optOutAnalytics`
// after so the live PostHog client immediately reflects the choice.
export function setConsent(value: ConsentValue): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value)
  } catch {
    // Storage blocked — choice won't persist across reloads but the
    // current-tab opt-in still works.
  }
}

export function initAnalytics() {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  if (!key) return
  analyticsConfigured = true
  // Default to PostHog's EU host. Override via VITE_POSTHOG_HOST if a
  // different region or self-hosted instance is in play.
  const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
    'https://eu.i.posthog.com'

  // The chunk name 'posthog' is set via vite.config.ts manualChunks.
  import('posthog-js')
    .then(({ default: posthog }) => {
      const consent = getConsent()
      posthog.init(key, {
        api_host: host,
        // Capture pageviews + clicks automatically. autocapture is
        // restricted to click events only — the default also records
        // typed-input events, which would land textarea content
        // (personal notes) and contenteditable selections (highlighted
        // lesson text fed to Deepen / Simplify) in PostHog ingest.
        // Funnel-specific questions are answered by explicit `track()`
        // calls from useAuth / CoursePlayer / Onboarding.
        capture_pageview: true,
        autocapture: { dom_event_allowlist: ['click'] },
        // Belt-and-suspenders: even on click events, PostHog records
        // the element's text. Mask it so a button label that quotes
        // user content (rare but possible — e.g. a "Re-open <quote>"
        // affordance) doesn't leak the quote.
        mask_all_text: true,
        // Respect Do-Not-Track.
        respect_dnt: true,
        // Defer cookie use — PostHog still works (uses localStorage
        // as a fallback) but we avoid putting analytics cookies on
        // the user's browser without a banner.
        persistence: 'localStorage+cookie',
        // Session replay is off pre-launch — see file header.
        disable_session_recording: true,
        // Consent gating (PECR): start opted-out so no events fire
        // until the user clicks Accept in CookieBanner. PostHog's
        // opt_out_capturing_by_default ensures even autocapture and
        // pageviews are suppressed until opt_in_capturing() runs.
        opt_out_capturing_by_default: true,
        loaded(ph) {
          ph.register({ build_mode: import.meta.env.MODE })
          if (consent === 'accepted') {
            ph.opt_in_capturing()
          }
        },
      })
      client = posthog
      for (const fn of queue) fn(posthog)
      queue.length = 0
    })
    .catch(() => {
      // Network error / blocked by tracker-blocker. Drop quietly —
      // analytics not landing should never break the app, and a 404
      // alert in Sentry on every adblock user would be noise.
      analyticsConfigured = false
      queue.length = 0
    })
}

// Called by CookieBanner when the user clicks Accept. Persists the choice
// and flips the live PostHog client into opt-in mode so events start
// flowing immediately — no page reload needed.
export function optInAnalytics(): void {
  setConsent('accepted')
  call((c) => c.opt_in_capturing())
}

// Called by CookieBanner when the user clicks Reject. Persists the choice
// and ensures the client is opted out (defensive — initAnalytics already
// starts opted-out, but covers the case where the user previously
// accepted and is now changing their mind).
export function optOutAnalytics(): void {
  setConsent('rejected')
  call((c) => c.opt_out_capturing())
}

// syncConsentFromServer reconciles the browser-local cookie_consent value
// against the authenticated user's account-scoped preference. Called by
// useAuth on bootstrap; the rules are:
//
//   * If the server has a value (analytics_consent != null), it WINS.
//     Mirror it to localStorage and update the live PostHog client so a
//     Reject made on phone immediately takes effect on the laptop.
//   * If the server has no value yet but localStorage does, push the
//     local choice up. This is the one-time migration path for users
//     whose only consent record predates the /me/preferences endpoint.
//   * Otherwise — both null, or no auth — do nothing.
//
// Network failures are swallowed; the SPA's existing state is fine to
// fall back on and the next page load will retry.
export async function syncConsentFromServer(client: PreferencesClient): Promise<void> {
  try {
    const prefs = await client.getPreferences()
    const local = getConsent()
    if (prefs.analytics_consent) {
      if (prefs.analytics_consent !== local) {
        if (prefs.analytics_consent === 'accepted') optInAnalytics()
        else optOutAnalytics()
      }
    } else if (local) {
      // Server is unset; push local up so future browsers inherit it.
      await client.updatePreferences({ analytics_consent: local })
    }
  } catch {
    // Network / 401 / 500 — keep whatever state we already have.
  }
}

// PreferencesClient is the narrow API surface syncConsentFromServer
// needs. Defined here so analytics.ts doesn't import the whole api
// module (which would create a tighter coupling between an utility
// and the HTTP client). Callers pass the api module's matching
// functions in.
export interface PreferencesClient {
  getPreferences: () => Promise<{ analytics_consent: 'accepted' | 'rejected' | null }>
  updatePreferences: (patch: { analytics_consent: 'accepted' | 'rejected' }) => Promise<unknown>
}

// Internal: dispatch a call to PostHog if it's loaded, queue it if
// it's configured-but-loading, drop it if analytics isn't configured.
function call(fn: (c: PostHog) => void) {
  if (!analyticsConfigured) return
  if (client) {
    fn(client)
    return
  }
  if (queue.length < QUEUE_CAP) queue.push(fn)
}

// Stitch the per-user identity onto the current session — call after
// successful login or registration. PostHog merges the anonymous
// session events that happened before this with the now-identified
// user, so the funnel from "first page view" to "module completed"
// remains attributable to one person.
export function identify(userId: string, props?: Record<string, unknown>) {
  call((c) => c.identify(userId, props))
}

// Drop the identified user — call from logout. Subsequent events are
// recorded under a fresh anonymous distinct_id until the next login.
export function reset() {
  call((c) => c.reset())
}

// Capture a custom event. Properties are optional and free-form.
// Naming convention: lowercase verb-phrase, snake_case (e.g.
// 'course_created', 'module_completed'). Mirrors PostHog's defaults
// so the events stay legible alongside autocapture entries.
export function track(event: string, props?: Record<string, unknown>) {
  call((c) => c.capture(event, props))
}
